import { useRef, useState } from "react";

import { getGeocode } from "use-places-autocomplete";

import { PROVINCIA_NOMBRE } from "utils/constants";

import { CENTRO_POR_DEFECTO } from "./googleMaps";

/**
 * Estado y logica de la ubicacion de una entidad: pin, vista del mapa y coordenada.
 *
 * Lo comparten los formularios de establecimiento (hospitales, CAPS, puestos, postas y URESA),
 * que son la misma entidad del back con distinto tipo de componente.
 *
 * La busqueda se dispara UNA vez, al elegir la localidad, que es el ultimo campo del domicilio:
 * ahi ya estan calle y numero, asi que es el momento en que la direccion esta completa. No se
 * geocodifica en cada tecla, que gastaba consultas mientras el usuario todavia escribia y movia
 * el pin justo despues de que lo acomodaba a mano.
 *
 * Y el resultado se toma como definitivo SOLO si Google devolvio la direccion. Cuando no le
 * consta la calle o la altura no contesta "no existe": devuelve igual el punto que mas se le
 * parece, el centro de la calle o de la localidad, y darlo por bueno guardaba una coordenada
 * equivocada que despues nadie sale a revisar. En ese caso el pin queda en la zona, como
 * referencia para arrastrarlo, pero la ubicacion sigue pendiente.
 */

/**
 * Seis decimales, como string.
 *
 * Devuelve string y no numero a proposito: es la misma implementacion que venia repetida en
 * los diez modales, y de aca sale lo que se muestra en los inputs de lat/lng y lo que viaja al
 * servicio. Redondear a numero cambiaria los ceros finales de los valores ya guardados
 * ("-40.810000" contra "-40.81").
 */
export const round6 = (valor) => Number(valor).toFixed(6);

/**
 * Estados del panel del mapa. La leyenda cambia segun donde esta el usuario en el circuito.
 */
export const LOCALIZACION = {
    /** Falta completar el domicilio, o falta que el usuario ponga el pin. */
    PENDIENTE: "pendiente",
    BUSCANDO: "buscando",
    /** Google devolvio la direccion: el pin esta en el lugar. */
    ENCONTRADA: "encontrada",
    /** Devolvio solo la zona: el pin es una referencia, no la ubicacion. */
    APROXIMADA: "aproximada",
    /** Se intento guardar sin haber definido la ubicacion. */
    FALTA_UBICAR: "faltaUbicar",
};

export const LEYENDAS = {
    [LOCALIZACION.PENDIENTE]:
        "Completá el domicilio: al elegir la localidad buscamos la dirección en el mapa.",
    [LOCALIZACION.BUSCANDO]: "Buscando la dirección...",
    [LOCALIZACION.ENCONTRADA]:
        "Ubicación encontrada. Si no es exacta, podés arrastrar el pin.",
    [LOCALIZACION.APROXIMADA]:
        "No encontramos esa dirección exacta. El pin quedó en la zona: arrastralo al lugar.",
    [LOCALIZACION.FALTA_UBICAR]:
        "Falta ubicar el pin en el mapa para poder guardar.",
};

/**
 * Los `types` con los que Google marca un resultado a nivel de direccion.
 *
 * Cualquier otro nivel es una zona y no una direccion: `route` es el centro de la calle
 * (encontro la calle pero no la altura) y `locality`, `sublocality`, `postal_code` o
 * `administrative_area_*` son el centro de la localidad o del departamento.
 *
 * Existe tambien `partial_match`, que Google prende cuando tuvo que corregir el texto. No se
 * usa a proposito: en direcciones argentinas se prende seguido por el nombre del departamento
 * aun cuando la altura es correcta, y daria por no encontradas direcciones que si lo estan.
 */
const TIPOS_DE_DIRECCION = [
    "street_address",
    "street_number",
    "premise",
    "subpremise",
];

const esDireccionExacta = (resultado) =>
    (resultado?.types ?? []).some((tipo) => TIPOS_DE_DIRECCION.includes(tipo));

const aPunto = (resultado) => ({
    lat: round6(resultado.geometry.location.lat()),
    lng: round6(resultado.geometry.location.lng()),
});

const primerResultado = async (consulta) => {
    // getGeocode hace `new window.google.maps.Geocoder` sin guarda: si el script de Google
    // todavia no se cargo, tira. Por eso se chequea antes en vez de comerse el error.
    if (!window.google?.maps) return null;

    try {
        const results = await getGeocode({ address: consulta });
        return results?.[0] ?? null;
    } catch (err) {
        // Ojo: getGeocode RECHAZA cuando Google responde ZERO_RESULTS, no devuelve una lista
        // vacia. Asi que "no la encontre" llega por aca y no por un resultado nulo: este catch
        // es el camino normal del caso sin coincidencia, no solo el de un error.
        return null;
    }
};

/**
 * Resuelve el domicilio a un punto: { lat, lng, exacta }, o null si no hubo nada.
 *
 * Dos intentos, en orden. Primero la direccion completa, que es la unica que cuenta como
 * ubicacion (`exacta: true`). Si Google no la reconoce a nivel de direccion, se pide la
 * localidad, que sirve de encuadre (`exacta: false`): geocodificar una localidad es seguro
 * —es una entidad administrativa con nombre propio, no una altura que puede no existir— y deja
 * el pin cerca para que el usuario lo arrastre lo menos posible.
 *
 * La provincia no es un campo del formulario: el panel administra una sola y entra como
 * constante. Igual va en la consulta, porque sin ella los nombres de localidad que se repiten
 * entre provincias resuelven en la otra.
 */
export const ubicarDomicilio = async ({ calle, numero, localidad }) => {
    const zona = [localidad, PROVINCIA_NOMBRE, "Argentina"]
        .filter(Boolean)
        .join(", ");

    if (calle && numero) {
        const resultado = await primerResultado(`${calle} ${numero}, ${zona}`);
        if (resultado && esDireccionExacta(resultado)) {
            return { ...aPunto(resultado), exacta: true };
        }
    }

    // La CALLE sola, antes de caer al centro de la localidad.
    //
    // Es el caso de una calle sin altura —que desde que el numero es opcional es un caso
    // normal, no una carga a medias— y tambien el de una altura que Google no conoce. Deja el
    // pin sobre la calle en vez de en el centro del pueblo, que es varias cuadras de arrastre
    // menos.
    //
    // Nunca es exacta: `route` es el centro del tramo, no la puerta.
    if (calle) {
        const resultado = await primerResultado(`${calle}, ${zona}`);
        if (resultado) return { ...aPunto(resultado), exacta: false };
    }

    if (!localidad) return null;

    const resultado = await primerResultado(zona);
    return resultado ? { ...aPunto(resultado), exacta: false } : null;
};

/**
 * @param esAlta        en el alta no hay coordenada elegida todavia; en la modificacion viene
 *                      del registro.
 * @param initialValues los valores del formulario, de donde salen lat y lng iniciales.
 * @param formikRef     para reflejar el punto en los campos lat y lng, que son de solo lectura.
 */
export const useUbicacion = ({ esAlta, initialValues, formikRef }) => {
    const puntoInicial = () => ({
        lat: round6(
            initialValues?.lat ? initialValues.lat : CENTRO_POR_DEFECTO.lat
        ),
        lng: round6(
            initialValues?.lng ? initialValues.lng : CENTRO_POR_DEFECTO.lng
        ),
    });

    // La posicion del pin, que es la coordenada que se guarda.
    const [coordenadas, setCoordenadas] = useState(puntoInicial);
    // A donde mira el mapa. Va aparte del pin: si la vista siguiera al pin, arrastrarlo al
    // borde haria saltar el mapa para centrarlo y se perderia la referencia.
    const [centro, setCentro] = useState(puntoInicial);

    // En la modificacion la coordenada ya viene del registro: no hay nada que buscar.
    const [estado, setEstado] = useState(
        esAlta ? LOCALIZACION.PENDIENTE : LOCALIZACION.ENCONTRADA
    );

    /**
     * Si la coordenada vale como la ubicacion del lugar.
     *
     * Arranca en false en el alta porque el pin nace en CENTRO_POR_DEFECTO, que es Viedma. Sin
     * este control, alguien que se olvida de ubicar guarda un puesto de Bariloche con las
     * coordenadas de Viedma y nada lo detecta: el back recibe dos numeros validos y el schema
     * de Yup no mira el mapa.
     *
     * La pasan a true solo dos cosas: una direccion encontrada a nivel de direccion, o el
     * usuario arrastrando el pin. El encuadre por localidad no, que deja el pin en el centro
     * del pueblo y no en la puerta del establecimiento.
     */
    const [definida, setDefinida] = useState(!esAlta);

    // Descarta la respuesta de una busqueda que ya no es la vigente: si se cambia de localidad
    // rapido, la anterior puede llegar despues y pisar el punto bueno.
    const busquedaPedida = useRef(0);

    const reflejarEnFormulario = (punto) => {
        // Los dos campos son de solo lectura y no estan en el schema: son el reflejo del pin.
        formikRef?.current?.setFieldValue("lat", punto.lat, false);
        formikRef?.current?.setFieldValue("lng", punto.lng, false);
    };

    /**
     * Lo dispara el select de localidad, y solo cuando la localidad CAMBIA.
     *
     * Cambiar de localidad invalida lo que hubiera elegido antes, incluso si la busqueda no
     * devuelve nada: esa coordenada era de otra localidad.
     */
    const ubicar = async (domicilio) => {
        setDefinida(false);
        setEstado(LOCALIZACION.BUSCANDO);

        const pedido = ++busquedaPedida.current;
        const punto = await ubicarDomicilio(domicilio);

        if (pedido !== busquedaPedida.current) return;

        // Sin punto el pin se queda donde estaba: no encuadrar es solo perder la comodidad.
        if (!punto) {
            setEstado(LOCALIZACION.PENDIENTE);
            return;
        }

        setCoordenadas(punto);
        setCentro(punto);
        reflejarEnFormulario(punto);

        if (punto.exacta) {
            setDefinida(true);
            setEstado(LOCALIZACION.ENCONTRADA);
            return;
        }

        setEstado(LOCALIZACION.APROXIMADA);
    };

    /**
     * El usuario arrastro el pin.
     *
     * Actualiza la coordenada y nada mas: NO se geocodifica al reves ni se toca ningun campo
     * de la direccion. El pin es para dejar la marca donde el usuario sabe que esta el lugar,
     * incluso cuando no coincide con lo que Google devuelve para esa direccion.
     *
     * La vista tampoco se mueve: el usuario ya la tenia donde queria.
     */
    const moverPin = ({ lat, lng }) => {
        const punto = { lat: round6(lat), lng: round6(lng) };
        setCoordenadas(punto);
        setDefinida(true);
        reflejarEnFormulario(punto);
        setEstado(LOCALIZACION.ENCONTRADA);
    };

    /** Lo llama el formulario cuando se intento guardar sin coordenada definida. */
    const marcarFaltaUbicar = () => setEstado(LOCALIZACION.FALTA_UBICAR);

    return {
        coordenadas,
        centro,
        estado,
        definida,
        ubicar,
        moverPin,
        marcarFaltaUbicar,
    };
};
